'use client';

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { Workflow, ShieldCheck, BrainCircuit, Lightbulb, ScanSearch, Sparkles, Zap, MessageSquareText, TriangleAlert, TrendingUp, Bot, Users, ChevronDown, Globe, BarChart3, Activity, ArrowRight } from 'lucide-react';
import BookDemoSection from '@/components/BookDemoSection';
import { BookDemoButton, Button, FaqAccordion } from '@/components/ui';
import AIEnginesSlider from '@/components/AIEnginesSlider';
import AIFeatureCards from '@/components/AIFeatureCards';

/* ── DATA ─────────────────────────────────────────────────────── */
/* Note: AI_FEATURES_GRID moved into /components/AIFeatureCards.jsx */
/* Note: AI_STACK moved into /components/AIEnginesSlider.jsx */
import { useTranslation } from "@/hooks/useTranslation";
const COPILOT_FEATURES = [{
  Icon: Zap,
  cap: 'Faster time-to-insight',
  title: 'Instant Intelligence',
  desc: 'Auto-summarises sentiment, themes, and intent so the right team acts immediately.'
}, {
  Icon: MessageSquareText,
  cap: 'Smart conversation drafting',
  title: 'Brand-Voice Replies at Scale',
  desc: 'Context-aware, brand-voice-consistent replies at volume with human approval in the loop.'
}, {
  Icon: TriangleAlert,
  cap: 'Proactive brand signals',
  title: 'Early Risk Detection',
  desc: 'Detects reputation risks and narrative shifts before they reach the news cycle.'
}, {
  Icon: TrendingUp,
  cap: 'First-contact resolution',
  title: 'Smarter Routing',
  desc: 'Intent detection routes the right case to the right agent. Fewer transfers, higher CSAT.'
}, {
  Icon: Bot,
  cap: 'Workflow automation',
  title: 'Zero Manual Overhead',
  desc: 'Tags, routes, escalates, and alerts without manual intervention eating your team\'s time.'
}, {
  Icon: Users,
  cap: 'Real-time agent coaching',
  title: 'Every Agent, Best Agent',
  desc: 'Live nudges on tone, empathy, and compliance turning every agent into your best one.'
}];
const AI_IN_ACTION = [{
  team: 'Social Team',
  color: '#7C3AED',
  bg: 'rgba(124,58,237,0.07)',
  challenge: 'Campaign drops flood us with mentions we cannot triage fast enough.',
  outcome: 'AI tags, prioritises, and drafts replies in bulk, clearing the queue in minutes.'
}, {
  team: 'CX Team',
  color: '#DC2626',
  bg: 'rgba(220,38,38,0.07)',
  challenge: 'We need to spot a brand crisis before it escalates.',
  outcome: 'SignalSense™ detects negative spikes and fires structured alerts with geo and severity context.'
}, {
  team: 'Marketing Team',
  color: '#1A6BFF',
  bg: 'rgba(26,107,255,0.07)',
  challenge: 'Which campaign is working and exactly why?',
  outcome: 'Theme clustering, volume trends, and sentiment maps that turn social data into campaign intelligence.'
}, {
  team: 'Support Team',
  color: '#059669',
  bg: 'rgba(5,150,105,0.07)',
  challenge: 'We want faster replies without losing the human touch.',
  outcome: 'ResponseGenie™ drafts pre-approved, tone-adaptive responses and agents focus on the hard calls.'
}];
const STATS = [{
  n: '60%',
  label: 'Faster response times via AI-suggested replies'
}, {
  n: '3x',
  label: 'Agent efficiency through smart triaging'
}, {
  n: '95%+',
  label: 'Tagging accuracy across 20+ industries'
}, {
  n: '350+',
  label: 'Enterprise & government brands across 8 countries'
}];
const FAQS = [{
  q: 'What is an AI-native customer experience platform?',
  a: 'An AI-native CX platform is one where AI is embedded into every workflow, not added as a feature layer. It means the platform listens, understands, responds, and learns automatically, without requiring manual configuration for each use case. Locobuzz is built this way from the ground up.'
}, {
  q: 'How does Locobuzz use AI for social listening?',
  a: "Locobuzz's SignalSense™ monitors 1M+ sources across 25+ channels in real time. It detects sentiment shifts, chatter spikes, and emerging brand narratives with geo-tagging and influencer-awareness built in. Alerts fire in under 5 minutes with near-zero false positives."
}, {
  q: 'What is the difference between ContextualPulse™ and standard sentiment analysis?',
  a: 'Standard sentiment analysis returns positive/negative/neutral. ContextualPulse™ reads aspect-level emotion, decodes sarcasm and implied meaning, classifies buyer intent, and maps loyalty signals. It understands what the customer meant, not just what they said.'
}, {
  q: 'How does AI improve agent response quality at scale?',
  a: 'AgentIQ™ coaches agents in real time with tone, empathy, and compliance nudges. ResponseGenie™ drafts on-brand, contextually accurate replies for agent approval. Together, they reduce AHT, improve first-contact resolution rates, and raise CSAT without adding headcount.'
}, {
  q: 'Is Locobuzz AI compliant with enterprise data security standards?',
  a: "Yes. Locobuzz is ISO 27001:2022 certified, SOC 2 Type II audited, and GDPR-compliant. It is also CERT-In ready and aligned with India's DPDP Act 2023, making it suitable for BFSI, healthcare, government, and other regulated industries."
}, {
  q: 'Can Locobuzz AI integrate with our existing CRM and helpdesk?',
  a: 'Locobuzz integrates bi-directionally with Salesforce, Microsoft Dynamics, Zendesk, Freshdesk, SAP, Slack, Teams, Tableau, and 1,000+ other tools. Data flows in real time, and every integration is governed and audit-ready.'
}];

/* ── ANIMATION VARIANTS ────────────────────────────────────────── */
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55
    }
  }
};
const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08
    }
  }
};

/* ── PAGE ─────────────────────────────────────────────────────── */
export default function AIStackPageClient() {
  const {
    t
  } = useTranslation();
  return <>
      {/* ───────────────────────────────────────────────────────────
          HERO — centered single column (matches reference)
          Diamond GIF now positioned BEHIND content (z-index 0) and
          pushed -8% right so it never overlaps the headline.
         ─────────────────────────────────────────────────────────── */}
      <section className="aistk-hero">
        <div className="aistk-hero__bg-glow" aria-hidden="true" />
        <div className="aistk-hero__bg-wave" aria-hidden="true" />

        <div className="aistk-hero__particles" aria-hidden="true">
          <span className="aistk-hero__particle" />
          <span className="aistk-hero__particle" />
          <span className="aistk-hero__particle" />
          <span className="aistk-hero__particle" />
          <span className="aistk-hero__particle" />
          <span className="aistk-hero__particle" />
          <span className="aistk-hero__particle" />
          <span className="aistk-hero__particle" />
        </div>

        <div className="container aistk-hero__inner">
          <motion.div className="aistk-hero__content" initial={{
          opacity: 0,
          y: 28
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.65
        }}>
            <h1 className="aistk-hero__h1">
              {t("hero.tagline_part_1")} <span className="aistk-hero__h1-grad">{t("ai_stack.aipowered_2")}</span> {t("ai_stack.customer_experience_2")}{' '}
              {t("ai_stack.platform_built_to_2")} <span className="aistk-hero__h1-grad--alt">{t("ai_stack.act_2")}</span> {t("ai_stack.not_just_analyse_2")}
            </h1>

            <p className="aistk-hero__sub">
              {t("ai_stack.most_platforms_add_ai_locobuzz_is_b_2")}
            </p>

            <div className="aistk-hero__ctas">
              <BookDemoButton label={t("ai_stack.talk_to_a_cx_expert_3")} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── STATS BAR ───────────────────────────────────────────── */}
      <div style={{
      background: 'linear-gradient(90deg,#0B1C3D,#1B2A5C)'
    }}>
        <div className="container">
          <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4,1fr)'
        }}>
            {STATS.map((s, i) => <div key={i} style={{
            padding: '34px 24px',
            borderRight: i < 3 ? '1px solid rgba(255,255,255,0.07)' : 'none',
            textAlign: 'center'
          }}>
                <div style={{
              fontFamily: "'Inter Tight',sans-serif",
              fontSize: 36,
              fontWeight: 800,
              letterSpacing: '-1px',
              lineHeight: 1,
              background: 'linear-gradient(90deg,#A78BFA,#60A5FA)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>{s.n}</div>
                <div style={{
              fontSize: 12,
              fontWeight: 600,
              color: 'rgba(255,255,255,0.45)',
              marginTop: 8,
              lineHeight: 1.4
            }}>{s.label}</div>
              </div>)}
          </div>
        </div>
      </div>

      {/* ───────────────────────────────────────────────────────────
          WHAT MAKES AI DIFFERENT — 6-card grid (NEW)
          Verlynk-reference visual style with custom SVG illustrations.
          All content lives in /components/AIFeatureCards.jsx
         ─────────────────────────────────────────────────────────── */}
      <AIFeatureCards />

      {/* ── AI COPILOT ──────────────────────────────────────────── */}
      <section className="lb-section" id="copilot">
        <div className="container">
          <div style={{
          textAlign: 'center',
          marginBottom: 52
        }}>
            <h2 className="lb-h2" style={{
            marginBottom: 14
          }}>{t("ai_stack.your_teams_ai_copilot_for_cx_2")}</h2>
            <p style={{
            fontSize: 16,
            color: '#4B5563',
            maxWidth: 520,
            margin: '0 auto',
            lineHeight: 1.7
          }}>{t("ai_stack.less_time_hunting_for_context_more_2")}</p>
          </div>
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true
        }} variants={stagger} className="lb-grid-3">
            {COPILOT_FEATURES.map(({
            Icon,
            cap,
            desc
          }, i) => <motion.div key={i} variants={fadeUp} whileHover={{
            y: -4,
            boxShadow: '0 12px 36px rgba(124,58,237,0.10)'
          }} style={{
            background: '#fff',
            border: '1px solid rgba(11,28,61,0.07)',
            borderRadius: 20,
            padding: '26px 24px',
            boxShadow: '0 2px 10px rgba(11,28,61,0.04)',
            transition: 'all 0.2s'
          }}>
                <div style={{
              width: 42,
              height: 42,
              borderRadius: 10,
              background: 'linear-gradient(135deg,rgba(124,58,237,0.12),rgba(26,107,255,0.12))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 14
            }}>
                  <Icon size={20} color="#7C3AED" />
                </div>
                <div style={{
              fontSize: 10,
              fontWeight: 800,
              color: '#7C3AED',
              textTransform: 'uppercase',
              letterSpacing: '0.6px',
              background: 'rgba(124,58,237,0.08)',
              padding: '3px 10px',
              borderRadius: 100,
              display: 'inline-block',
              marginBottom: 10,
              border: '1px solid rgba(124,58,237,0.15)'
            }}>{cap}</div>
                <div style={{
              fontSize: 13,
              color: '#4B5563',
              lineHeight: 1.65
            }}>{desc}</div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ───────────────────────────────────────────────────────────
          AI ENGINES — Swiper slider (NEW)
          All slide content lives in /components/AIEnginesSlider.jsx
          Background: wave-graph-bg.jpg + white overlay
         ─────────────────────────────────────────────────────────── */}
      <AIEnginesSlider />

      {/* AIShowcaseSlider removed — was a duplicate "6 AI engines" slider
          below the primary AIEnginesSlider (same content, different style).
          Component file kept in /components for future revival. */}

      {/* ── AI IN ACTION ────────────────────────────────────────── */}
      <section className="lb-section" id="action">
        <div className="container">
          <div style={{
          textAlign: 'center',
          marginBottom: 52
        }}>
            <span className="lb-pill" style={{
            marginBottom: 14
          }}>{t("ai_stack.ai_in_action_2")}</span>
            <h2 className="lb-h2" style={{
            marginBottom: 14
          }}>{t("ai_stack.real_challenges_real_ai_outcomes_2")}</h2>
            <p style={{
            fontSize: 16,
            color: '#4B5563',
            maxWidth: 480,
            margin: '0 auto',
            lineHeight: 1.7
          }}>{t("ai_stack.heres_how_different_teams_inside_yo_2")}</p>
          </div>
          <motion.div className="aistk-action-grid" initial="hidden" whileInView="show" viewport={{
          once: true
        }} variants={stagger} style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2,1fr)',
          gap: 20
        }}>
            {AI_IN_ACTION.map((card, i) => <motion.div key={i} variants={fadeUp} whileHover={{
            y: -4,
            boxShadow: '0 12px 36px rgba(11,28,61,0.10)'
          }} style={{
            borderRadius: 22,
            padding: 30,
            border: '1px solid rgba(11,28,61,0.08)',
            background: '#fff',
            boxShadow: '0 2px 12px rgba(11,28,61,0.04)',
            transition: 'all 0.2s'
          }}>
                <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 11,
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              padding: '4px 12px',
              borderRadius: 100,
              marginBottom: 18,
              background: card.bg,
              color: card.color,
              border: '1px solid ' + card.color + '33'
            }}>{card.team}</div>
                <div style={{
              fontSize: 14,
              color: '#4B5563',
              fontStyle: 'italic',
              lineHeight: 1.6,
              padding: '12px 14px',
              background: '#F5F7FF',
              borderRadius: 10,
              borderLeft: '3px solid rgba(124,58,237,0.25)',
              marginBottom: 16
            }}>"{card.challenge}"</div>
                <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 12
            }}>
                  <div style={{
                flex: 1,
                height: 1,
                background: 'rgba(11,28,61,0.08)'
              }} />
                  <div style={{
                fontSize: 9,
                fontWeight: 800,
                background: 'linear-gradient(135deg,#7C3AED,#1A6BFF)',
                color: 'white',
                padding: '2px 8px',
                borderRadius: 4,
                letterSpacing: '0.4px',
                whiteSpace: 'nowrap'
              }}>{t("ai_stack.what_ai_does_2")}</div>
                  <div style={{
                flex: 1,
                height: 1,
                background: 'rgba(11,28,61,0.08)'
              }} />
                </div>
                <div style={{
              fontSize: 14,
              color: '#0B1C3D',
              fontWeight: 600,
              lineHeight: 1.6
            }}>{card.outcome}</div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────── */}
      <section className="lb-section" id="faq">
        <div className="container">
          <div style={{
          textAlign: 'center',
          marginBottom: 52
        }}>
            <h2 className="lb-h2" style={{
            marginBottom: 0
          }}>{t("ai_stack.questions_teams_ask_before_choosing_2")}</h2>
          </div>
          <div style={{
          maxWidth: 800,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 12
        }}>
            <FaqAccordion items={FAQS.map(faq => ({ q: faq.q, a: faq.a }))} />
          </div>
        </div>
      </section>

      {/* ── CLOSING CTA ─────────────────────────────────────────── */}
      <section style={{
      background: 'linear-gradient(135deg,#F5F3FF 0%,#EEF2FF 50%,#F0F9FF 100%)',
      position: 'relative',
      padding: '112px 0',
      overflow: 'hidden'
    }}>
        <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'radial-gradient(rgba(124,58,237,0.07) 1px, transparent 1px)',
        backgroundSize: '32px 32px',
        pointerEvents: 'none'
      }} />
        <div style={{
        position: 'absolute',
        width: 700,
        height: 700,
        borderRadius: '50%',
        top: -200,
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'radial-gradient(circle,rgba(124,58,237,0.10) 0%,transparent 70%)',
        filter: 'blur(60px)',
        pointerEvents: 'none'
      }} />
        <div className="container" style={{
        position: 'relative',
        zIndex: 2,
        textAlign: 'center',
        maxWidth: 700,
        margin: '0 auto'
      }}>
          <motion.h2 initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} style={{
          fontFamily: "'Inter Tight',sans-serif",
          fontSize: 'clamp(30px,4vw,48px)',
          fontWeight: 800,
          color: '#0D0F1A',
          letterSpacing: '-1.4px',
          marginBottom: 18,
          lineHeight: 1.1
        }}>
            {t("ai_stack.cx_that_2")}{' '}
            <span style={{
            background: 'linear-gradient(90deg,#7C3AED,#4F46E5)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>{t("ai_stack.thinks_ahead_2")}</span>{' '}
            {t("ai_stack.starting_now_2")}
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} style={{
          fontSize: 17,
          color: '#4B5563',
          fontWeight: 500,
          lineHeight: 1.7,
          marginBottom: 40
        }}>
            {t("ai_stack.understanding_your_customers_has_ne_2")}
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 14,
          flexWrap: 'wrap'
        }}>
            <BookDemoButton label={t("ai_stack.talk_to_a_cx_expert_4")} />
            <Button href="#engines" variant="secondary" size="lg">{t("ai_stack.explore_the_ai_stack_2")}</Button>
          </motion.div>
        </div>
      </section>

      <BookDemoSection />
    </>;
}